import Loader from "components/Loader";
import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { dispatch } from "store";
import { getInvoicePdfThunk } from "store/reducers/invoice/invoiceSlice";
import LightLogo from "assets/logo.png";
import { isMobile, isBrowser } from "react-device-detect";
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Image,
  Font,
  PDFViewer,
  Svg,
  Line,
  PDFDownloadLink,
} from "@react-pdf/renderer";
import { Box, Typography } from "@mui/material";
import convertToWords from "convert-rupees-into-words";
import moment from "moment";
import JsBarcode from "jsbarcode";

const InvoicePdf = () => {
  const { uuid } = useParams();
  useEffect(() => {
    dispatch(getInvoicePdfThunk(uuid));
  }, [uuid]);
  useEffect(() => {
    document.title = "Vendzee Invoice";
  }, []);
  const state = useSelector((state) => state.invoicePdf);
  const canvas = document.createElement("canvas");
  JsBarcode(canvas, window.location.href);
  const barcode = canvas.toDataURL();

  const Divider = ({ style }) => (
    <Svg height="5" width="100%" style={style}>
      <Line x1="0" y1="5" x2="900" y2="5" strokeWidth={2} stroke="rgb(0,0,0)" />
    </Svg>
  );

  Font.register({
    family: "Open Sans",
    fonts: [
      {
        src: "https://cdn.jsdelivr.net/npm/open-sans-all@0.1.3/fonts/open-sans-regular.ttf",
      },
      {
        src: "https://cdn.jsdelivr.net/npm/open-sans-all@0.1.3/fonts/open-sans-600.ttf",
        fontWeight: 600,
      },
    ],
  });

  const styles = StyleSheet.create({
    page: {
      backgroundColor: "white",
      color: "black",
      flexDirection: "column",
      paddingVertical: 20,
      paddingHorizontal: 40,
    },

    viewer: {
      width: window.innerWidth,
      height: window.innerHeight,
    },
    logo: {
      width: 130,
      alignSelf: "center",
    },
    text: {
      fontFamily: "Open Sans",
      fontSize: 10,
      marginTop: 5,
    },
  });

  const InvoiceDocument = () => (
    <Document>
      <Page size="A4" style={styles.page}>
        <Image
          style={styles.logo}
          source={state.data.invoice_config?.logo ?? LightLogo}
        />
        <View
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            marginTop: 10,
          }}
        >
          <View>
            <Text
              style={{
                fontSize: 12,
                lineHeight: 1.5,
                fontFamily: "Open Sans",
              }}
            >
              {state.data.invoice_config?.address?.replace(/\r/g, "") ?? ""}
            </Text>
            <Text
              style={{
                fontSize: 13,
                marginTop: 10,
                fontFamily: "Open Sans",
                fontWeight: 800,
              }}
            >
              GSTIN: {state.data.invoice_config?.GSTIN}
            </Text>
          </View>
          <View
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
            }}
          >
            <Text
              style={{
                fontSize: 10,
                fontWeight: 800,
                fontFamily: "Open Sans",
                textAlign: "right",
              }}
            >
              INVOICE NO : {state.data?.order_no ?? ""}
            </Text>
            <Image src={barcode} style={{ width: 180, height: 50 }} />
          </View>
        </View>
        <Text
          style={{
            fontSize: 18,
            fontWeight: 800,
            fontFamily: "Open Sans",
            alignSelf: "center",
            marginTop: 10,
          }}
        >
          TAX INVOICE
        </Text>
        <Divider style={{ marginTop: 5 }} />
        <View
          style={{
            display: "flex",
            flexDirection: "row",
            marginTop: 5,
            marginBottom: 5,
          }}
        >
          <View
            style={{
              flex: 1,
            }}
          >
            <Text style={{ ...styles.text, fontSize: 12, textAlign: "left" }}>
              S.NO
            </Text>
          </View>
          <View
            style={{
              flex: 4,
            }}
          >
            <Text
              style={{
                ...styles.text,
                fontSize: 12,
                textAlign: "center",
              }}
            >
              ITEM NAME
            </Text>
          </View>
          <View
            style={{
              flex: 3,
            }}
          >
            <Text
              style={{
                ...styles.text,
                fontSize: 12,
                textAlign: "center",
              }}
            >
              UNIT PRICE
            </Text>
          </View>
          <View
            style={{
              flex: 1,
            }}
          >
            <Text
              style={{
                ...styles.text,
                fontSize: 12,
                textAlign: "center",
              }}
            >
              QTY
            </Text>
          </View>
          <View style={{ flex: 1 }}>
            <Text
              style={{
                ...styles.text,
                fontSize: 12,
                textAlign: "right",
              }}
            >
              VALUE
            </Text>
          </View>
        </View>
        <Divider />
        {state.data.invoices.map((invoice, index) => (
          <View
            style={{
              display: "flex",
              flexDirection: "row",
              marginTop: 5,
              marginBottom: 5,
            }}
            key={index}
          >
            <View
              style={{
                flex: 1,
              }}
            >
              <Text
                style={{
                  ...styles.text,
                  fontSize: 10,
                  textAlign: "left",
                  marginTop: 0,
                  marginLeft: 10,
                }}
              >
                {index + 1}
              </Text>
            </View>
            <View
              style={{
                flex: 4,
              }}
            >
              <Text
                style={{
                  ...styles.text,
                  fontSize: 10,
                  textAlign: "center",
                  marginTop: 0,
                }}
              >
                {invoice.persistent_data?.product_name ?? invoice.product_name}
              </Text>
            </View>
            <View
              style={{
                flex: 3,
              }}
            >
              <Text
                style={{
                  ...styles.text,
                  fontSize: 10,
                  textAlign: "center",
                  marginTop: 0,
                }}
              >
                {(
                  invoice.unit_price -
                  invoice.discount_amount / invoice.qty
                ).toFixed(2)}
              </Text>
            </View>
            <View
              style={{
                flex: 1,
              }}
            >
              <Text
                style={{
                  ...styles.text,
                  fontSize: 10,
                  textAlign: "center",
                  marginTop: 0,
                }}
              >
                {invoice.qty}
              </Text>
            </View>
            <View style={{ flex: 1 }}>
              <Text
                style={{
                  ...styles.text,
                  fontSize: 10,
                  textAlign: "right",
                  marginTop: 0,
                  marginRight: 5,
                }}
              >
                {(
                  invoice.unit_price * invoice.qty -
                  invoice.discount_amount
                ).toFixed(2)}
              </Text>
            </View>
          </View>
        ))}
        <Divider />
        <View
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
          }}
        >
          <View>
            <Text style={styles.text}>TOTAL AMOUNT</Text>
            <Text style={styles.text}>TOTAL ITEMS SOLD</Text>
            <Text style={{ ...styles.text, marginTop: 10, fontSize: 10 }}>
              Payment summary : Prices inclusive of all taxes
            </Text>
            <Text
              style={{
                ...styles.text,
                marginTop: 10,
                fontSize: 10,
                fontWeight: 800,
              }}
            >
              {convertToWords(state.data.payable_amount)} Only
            </Text>
          </View>
          <View>
            <Text style={{ ...styles.text, marginRight: 5 }}>
              {state.data.payable_amount.toFixed(2)}
            </Text>
            <Text style={{ ...styles.text, textAlign: "center" }}>
              {state.data.qty} Nos
            </Text>
          </View>
        </View>
        <Divider style={{ marginTop: 5 }} />
        <Text
          style={{
            ...styles.text,
            marginTop: 5,
            marginBottom: 5,
            fontSize: 15,
            fontWeight: 800,
            textAlign: "center",
          }}
        >
          You have saved Rs {state.data.total_discount.toFixed(2)} in this visit
        </Text>
        <Divider />
        {state.data.hsn_calc && (
          <>
            <View
              style={{
                display: "flex",
                flexDirection: "row",
                marginTop: 5,
                marginBottom: 5,
              }}
            >
              <View
                style={{
                  flex: 3,
                }}
              >
                <Text
                  style={{
                    ...styles.text,
                    fontSize: 12,
                    textAlign: "left",
                  }}
                >
                  HSN CODE
                </Text>
              </View>
              <View
                style={{
                  flex: 3,
                }}
              >
                <Text
                  style={{
                    ...styles.text,
                    fontSize: 12,
                    textAlign: "center",
                  }}
                >
                  TAXABLE AMOUNT
                </Text>
              </View>
              <View
                style={{
                  flex: 2,
                }}
              >
                <Text
                  style={{
                    ...styles.text,
                    fontSize: 12,
                    textAlign: "center",
                  }}
                >
                  CGST
                </Text>
              </View>
              <View
                style={{
                  flex: 2,
                }}
              >
                <Text
                  style={{
                    ...styles.text,
                    fontSize: 12,
                    textAlign: "center",
                  }}
                >
                  SGST
                </Text>
              </View>
              <View style={{ flex: 2 }}>
                <Text
                  style={{
                    ...styles.text,
                    fontSize: 12,
                    textAlign: "right",
                  }}
                >
                  CESS
                </Text>
              </View>
            </View>
            <Divider />
            {state.data.hsn_calc.map((hsn, index) => (
              <View
                style={{
                  display: "flex",
                  flexDirection: "row",
                  marginTop: 5,
                  marginBottom: 5,
                }}
                key={index}
              >
                <View
                  style={{
                    flex: 3,
                  }}
                >
                  <Text
                    style={{
                      ...styles.text,
                      fontSize: 10,
                      textAlign: "left",
                      marginLeft: 10,
                    }}
                  >
                    {hsn.hsn_code}
                  </Text>
                </View>
                <View
                  style={{
                    flex: 3,
                  }}
                >
                  <Text
                    style={{
                      ...styles.text,
                      fontSize: 10,
                      textAlign: "center",
                    }}
                  >
                    {hsn.taxable_amount.toFixed(2)}
                  </Text>
                </View>
                <View
                  style={{
                    flex: 2,
                  }}
                >
                  <Text
                    style={{
                      ...styles.text,
                      fontSize: 10,
                      textAlign: "center",
                    }}
                  >
                    {" "}
                    {hsn.cgst > 0
                      ? `${hsn.cgst_total} \n (${hsn.cgst}% CGST)`
                      : 0.0}
                  </Text>
                </View>
                <View
                  style={{
                    flex: 2,
                  }}
                >
                  <Text
                    style={{
                      ...styles.text,
                      fontSize: 10,
                      textAlign: "center",
                    }}
                  >
                    {" "}
                    {hsn.sgst > 0
                      ? `${hsn.sgst_total} \n (${hsn.sgst}% SGST)`
                      : 0.0}
                  </Text>
                </View>
                <View style={{ flex: 2 }}>
                  <Text
                    style={{
                      ...styles.text,
                      fontSize: 10,
                      textAlign: "right",
                      marginRight: 10,
                    }}
                  >
                    {" "}
                    {hsn.cess > 0
                      ? `${hsn.cess_total} \n (${hsn.cess}% CESS)`
                      : 0.0}
                  </Text>
                </View>
              </View>
            ))}
          </>
        )}
        <Divider />
        <View
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
          }}
        >
          <View>
            <Text
              style={{
                ...styles.text,
                fontSize: 11,
              }}
            >
              Date :{" "}
              {moment(state.data.created_date).format("DD/MM/YYYY HH:mm:ss")}
            </Text>
            <Text
              style={{
                ...styles.text,
                fontSize: 11,
              }}
            >
              Store : {state.data.machine_code}
            </Text>
            <Text
              style={{
                ...styles.text,
                fontSize: 11,
              }}
            >
              Payment reference ID : {state.data.payment_reference_id}
            </Text>
          </View>
          <View
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-end",
            }}
          >
            <Text
              style={{
                ...styles.text,
                fontSize: 11,
              }}
            >
              Payment transaction ID :{state.data.payment_transaction_id}
            </Text>
          </View>
        </View>
        <Divider />
        <Text
          style={{
            ...styles.text,
            fontSize: 11,
            textAlign: "left",
            fontWeight: 800,
          }}
        >
          Terms & Conditions apply
        </Text>
        <Text
          style={{
            ...styles.text,
            fontSize: 11,
            textAlign: "left",
          }}
        >
          Amount inclusive of applicable taxes{"\n"}
          Customer care : {state.data.invoice_config?.phone} {"\n"}
          Email:{state.data.invoice_config?.email}
        </Text>

        <Text
          style={{
            ...styles.text,
            fontSize: 11,
            textAlign: "center",
            marginTop: 10,
          }}
        >
          Please refer to our website link
        </Text>
        <Text
          style={{
            ...styles.text,
            fontSize: 12,
            textAlign: "center",
            marginTop: 10,
            fontWeight: 800,
          }}
        >
          {state.data.invoice_config?.web}
        </Text>
      </Page>
    </Document>
  );
  return (
    <>
      {state.loading && <Loader />}
      {state.error && (
        <Box padding={3}>
          <Typography variant="h5">Unable to load invoice !!</Typography>
        </Box>
      )}
      {state.success && state.data && (
        <>
          {isBrowser ? (
            <PDFViewer style={styles.viewer}>
              <InvoiceDocument />
            </PDFViewer>
          ) : (
            <PDFDownloadLink
              document={<InvoiceDocument />}
              fileName={`${state.data.uuid}.pdf`}
            >
              {({ blob, url, loading, error }) =>
                loading ? "Getting invoice..." : "Download invoice!"
              }
            </PDFDownloadLink>
          )}
        </>
      )}
    </>
  );
};

export default InvoicePdf;
